import type { Group, Pouch, Sheet } from '../types';
import {
  isInMuteGroup,
  pouchCountsAsTimeForSheetDisplay,
} from './GroupLogic';
import {
  getLayerEntry,
  getPouchLayerIsTime,
  getSortedLayers,
  mirrorPouchesForLayer,
  resolveActiveLayerId,
  syncPouchValueMirror,
} from './LayerLogic';

/** BP 以外の合計寄与パウチがすべて時間表示相当か（GTP は参照先の葉がすべて時間なら時間） */
export function sheetNonBalancingContributorsAreAllTime(
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
): boolean {
  const contributing = pouches.filter(
    (p) =>
      p.id !== balancingPouchId &&
      !p._isDeleting &&
      !p._isGtpRenderPreview &&
      !isInMuteGroup(p.id, groups),
  );
  if (contributing.length === 0) {
    return false;
  }
  return contributing.every((p) =>
    pouchCountsAsTimeForSheetDisplay(p, pouches, groups, balancingPouchId),
  );
}

function shouldBalancingPouchBeTimeOnLayer(
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  layerId: string,
  sheet: Sheet,
): boolean {
  const mirrored = mirrorPouchesForLayer(pouches, layerId, sheet, groups);
  return sheetNonBalancingContributorsAreAllTime(mirrored, groups, balancingPouchId);
}

/**
 * BP の isTime をシート／レイヤー上の他パウチに合わせる（applyBalancingPouch の前に呼ぶ）。
 * すべて時間 → BP も時間、1 つでも非時間 → BP も数値。
 */
export function syncBalancingPouchTimeMode(
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  sheet?: Sheet,
): Pouch[] {
  const idx = pouches.findIndex((p) => p.id === balancingPouchId);
  if (idx < 0) {
    return pouches;
  }

  let bp = pouches[idx];
  let changed = false;

  if (sheet?.layers?.length) {
    for (const layer of getSortedLayers(sheet)) {
      const shouldBeTime = shouldBalancingPouchBeTimeOnLayer(
        pouches,
        groups,
        balancingPouchId,
        layer.id,
        sheet,
      );
      const isTimeNow = getPouchLayerIsTime(bp, layer.id);
      if (shouldBeTime === isTimeNow) {
        continue;
      }
      changed = true;
      const layerValues = { ...(bp.layerValues ?? {}) };
      layerValues[layer.id] = {
        ...getLayerEntry(bp, layer.id),
        isTime: shouldBeTime,
      };
      bp = { ...bp, layerValues };
    }
    if (changed) {
      bp = syncPouchValueMirror(bp, resolveActiveLayerId(sheet), sheet);
    }
  } else {
    const shouldBeTime = sheetNonBalancingContributorsAreAllTime(
      pouches,
      groups,
      balancingPouchId,
    );
    const isTimeNow = bp.isTime === true;
    if (shouldBeTime !== isTimeNow) {
      changed = true;
      bp = { ...bp, isTime: shouldBeTime };
    }
  }

  if (!changed) {
    return pouches;
  }
  return pouches.map((p) => (p.id === balancingPouchId ? bp : p));
}
